import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@cca/ui';
import { AlertTriangle } from 'lucide-react';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface Contradiction {
  readonly code: string;
  readonly explanation: string;
  readonly evidence: readonly string[];
}

interface BriefAction {
  readonly id: string;
  readonly sequence: number;
  readonly title: string;
  readonly audience_type: string;
  readonly communication_class: string;
  readonly subject: string | null;
  readonly body_text: string | null;
  readonly estimated_recipient_count: number | null;
  readonly scheduled_date: string | null;
  readonly risk_level: string;
  readonly status: string;
  readonly assumptions: readonly string[] | null;
  readonly missing_information: readonly string[] | null;
  readonly blocked_reasons: readonly string[] | null;
}

interface BriefSummary {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  readonly contradictions: readonly Contradiction[] | null;
  readonly created_at: string;
}

interface BriefDetail extends BriefSummary {
  readonly raw_text: string;
  readonly communication_brief_actions: readonly BriefAction[] | null;
}

const EXAMPLE_BRIEF = [
  '1. Customers with orders 1041 and 1044 need to be informed about a delay.',
  '2. Affiliates must receive the new commission information.',
  '3. Partners must be told that production is now three weeks.',
  '4. Prepare a re-engagement campaign for customers inactive for 90 days.',
  '5. Do not send anything without approval.',
].join('\n');

const AUDIENCE_TONE: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  CUSTOMERS: 'info',
  AFFILIATES: 'success',
  PARTNERS: 'warning',
  SUPPLIERS: 'neutral',
  INTERNAL: 'neutral',
};

/**
 * The parsed breakdown of one brief.
 *
 * Blocked actions are shown, not hidden. A staff member needs to see what the
 * agent proposed and why it cannot run, otherwise the contradiction panel reads
 * as an unexplained refusal.
 */
function ActionCard({ action }: { readonly action: BriefAction }) {
  const blocked = action.status === 'BLOCKED';

  return (
    <Card className={blocked ? 'border-amber-300 dark:border-amber-700' : undefined}>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>
            {action.sequence}. {action.title}
          </CardTitle>
          <CardDescription>
            {action.communication_class.toLowerCase()}
            {action.estimated_recipient_count != null
              ? ` · ~${action.estimated_recipient_count} recipient${action.estimated_recipient_count === 1 ? '' : 's'}`
              : ''}
            {action.scheduled_date ? ` · ${action.scheduled_date}` : ''}
          </CardDescription>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone={AUDIENCE_TONE[action.audience_type] ?? 'neutral'}>
            {action.audience_type.toLowerCase()}
          </Badge>
          {blocked ? <Badge tone="danger">blocked</Badge> : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        {action.subject ? (
          <p className="text-sm">
            <span className="text-slate-500 dark:text-slate-400">Subject: </span>
            {action.subject}
          </p>
        ) : null}

        {action.body_text ? (
          <p className="rounded-md border border-slate-200 bg-white p-3 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
            {action.body_text}
          </p>
        ) : null}

        {action.missing_information && action.missing_information.length > 0 ? (
          <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-950">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Missing information
            </p>
            <ul className="mt-1 space-y-0.5">
              {action.missing_information.map((item, index) => (
                <li key={index} className="text-xs text-slate-600 dark:text-slate-400">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {blocked && action.blocked_reasons && action.blocked_reasons.length > 0 ? (
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Blocked by: {action.blocked_reasons.map((r) => r.replaceAll('_', ' ')).join(', ')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BriefDetailPane({
  briefId,
  accessToken,
}: {
  readonly briefId: string;
  readonly accessToken: string | null;
}) {
  const queryClient = useQueryClient();

  const brief = useQuery({
    queryKey: ['brief', briefId],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<BriefDetail>(`/api/briefs/${briefId}`, { accessToken }),
  });

  /**
   * Parsing is what turns free text into actions. In simulator mode there is no
   * language model, so the breakdown posted here is a fixed one — enough to
   * exercise the contradiction scanner, which is the part that must be right.
   */
  const parse = useMutation({
    mutationFn: () =>
      apiRequest<{ canProceed: boolean; contradictions: Contradiction[]; actionCount: number }>(
        `/api/briefs/${briefId}/parse`,
        {
          method: 'POST',
          accessToken,
          body: {
            actions: [
              {
                sequence: 1,
                title: 'Delay update for affected customers',
                audienceType: 'CUSTOMERS',
                communicationClass: 'OPERATIONAL',
                subject: 'An update on your order',
                bodyText: 'Your order is taking longer than usual and we are checking on it.',
                estimatedRecipientCount: 2,
              },
              {
                sequence: 2,
                title: 'Affiliate commission update',
                audienceType: 'AFFILIATES',
                communicationClass: 'OPERATIONAL',
                subject: 'New commission rates',
                bodyText: 'Commission changes from 8 to 10 percent on 1 September.',
                estimatedRecipientCount: 1,
              },
            ],
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['brief', briefId] });
      void queryClient.invalidateQueries({ queryKey: ['briefs'] });
    },
  });

  if (brief.isPending) return <LoadingState label="Loading brief" />;
  if (brief.isError) {
    return (
      <ErrorState
        description={brief.error instanceof ApiError ? brief.error.message : 'Could not load.'}
        onRetry={() => void brief.refetch()}
      />
    );
  }

  const contradictions = brief.data.contradictions ?? [];
  const actions = brief.data.communication_brief_actions ?? [];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {brief.data.title}
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {brief.data.status.toLowerCase()}
          </p>
        </div>
        <Button variant="outline" disabled={parse.isPending} onClick={() => parse.mutate()}>
          {parse.isPending ? 'Parsing…' : 'Parse into actions'}
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>What was written</CardTitle>
          <CardDescription>
            Kept verbatim so the interpretation below can be checked against it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
            {brief.data.raw_text}
          </p>
        </CardContent>
      </Card>

      {contradictions.length > 0 ? (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
              <CardTitle>This brief contradicts itself</CardTitle>
            </div>
            <CardDescription>
              Nothing has been scheduled. Resolve these and parse again.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {contradictions.map((contradiction, index) => (
              <div key={index} role="alert">
                <p className="text-sm text-slate-800 dark:text-slate-200">
                  {contradiction.explanation}
                </p>
                {contradiction.evidence.length > 0 ? (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    In the brief: {contradiction.evidence.map((e) => `"${e}"`).join(', ')}
                  </p>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {parse.isError ? (
        <ErrorState
          title="Could not parse"
          description={parse.error instanceof ApiError ? parse.error.message : 'Unexpected error.'}
        />
      ) : null}

      {actions.length > 0 ? (
        <section aria-label="Proposed actions" className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Proposed actions
          </h3>
          {[...actions]
            .sort((a, b) => a.sequence - b.sequence)
            .map((action) => (
              <ActionCard key={action.id} action={action} />
            ))}
        </section>
      ) : (
        <Card>
          <EmptyState
            title="Not parsed yet"
            description="Parse the brief to see it broken into separate proposed actions."
          />
        </Card>
      )}
    </div>
  );
}

function BriefsPage() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [text, setText] = useState(EXAMPLE_BRIEF);

  const briefs = useQuery({
    queryKey: ['briefs'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<{ briefs: BriefSummary[] }>('/api/briefs', { accessToken }),
  });

  const create = useMutation({
    mutationFn: (rawText: string) =>
      apiRequest<{ briefId: string }>('/api/briefs', {
        method: 'POST',
        accessToken,
        body: { rawText },
      }),
    onSuccess: (data) => {
      setSelected(data.briefId);
      void queryClient.invalidateQueries({ queryKey: ['briefs'] });
    },
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Weekly Briefs</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Write the week's communications in plain text. They are broken into separate actions you
          can review one at a time.
        </p>
      </header>

      <Card>
        <CardContent className="space-y-3">
          <label htmlFor="brief" className="block text-sm font-medium">
            New brief
          </label>
          <textarea
            id="brief"
            rows={7}
            value={text}
            onChange={(event) => setText(event.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white p-3 font-mono text-sm focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-slate-700 dark:bg-slate-950"
          />
          <div className="flex items-center gap-3">
            <Button
              disabled={create.isPending || text.trim().length < 5}
              onClick={() => create.mutate(text)}
            >
              {create.isPending ? 'Saving…' : 'Create brief'}
            </Button>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Creating a brief sends nothing.
            </span>
          </div>

          {create.isError ? (
            <p role="alert" className="text-sm text-red-700 dark:text-red-400">
              {create.error instanceof ApiError ? create.error.message : 'Could not create.'}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[18rem_1fr]">
        <Card className="overflow-hidden p-0">
          {briefs.isPending ? <LoadingState label="Loading briefs" /> : null}
          {briefs.data && briefs.data.briefs.length === 0 ? (
            <EmptyState title="No briefs yet" description="Create one above to get started." />
          ) : null}

          <ul>
            {(briefs.data?.briefs ?? []).map((brief) => (
              <li key={brief.id}>
                <button
                  type="button"
                  onClick={() => setSelected(brief.id)}
                  aria-current={selected === brief.id ? 'true' : undefined}
                  className={`w-full border-b border-slate-200 px-3 py-3 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-slate-800 ${
                    selected === brief.id
                      ? 'bg-sky-50 dark:bg-sky-950'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-900'
                  }`}
                >
                  <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                    {brief.title}
                  </span>
                  <span className="mt-1 flex items-center gap-2">
                    <Badge tone={(brief.contradictions ?? []).length > 0 ? 'warning' : 'neutral'}>
                      {brief.status.toLowerCase()}
                    </Badge>
                    {(brief.contradictions ?? []).length > 0 ? (
                      <span className="text-xs text-amber-700 dark:text-amber-300">
                        {(brief.contradictions ?? []).length} contradiction
                        {(brief.contradictions ?? []).length === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <div>
          {selected ? (
            <BriefDetailPane briefId={selected} accessToken={accessToken} />
          ) : (
            <Card>
              <EmptyState
                title="Select a brief"
                description="Choose one on the left to see its parsed actions and any contradictions."
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export const briefsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/briefs',
  component: () => (
    <RequireSession>
      <BriefsPage />
    </RequireSession>
  ),
});
